/**
 * BudgetAlertBanner — soft in-app alert for the AI Consumption Console (P3).
 * Renders a `role="alert"` banner when any budget dimension — daily or monthly,
 * USD *or* token ceiling — is `approaching` or `over`; renders nothing when
 * every dimension is `under`. Colour follows the worst level via `--g-*` tokens
 * (no hex fallback — `--g-danger`/`--g-warn` are defined in
 * @garden/config-tailwind).
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ADMIN_NS } from "@/features/admin/api";
import type { BudgetStatusResponse } from "@/features/admin/ai-usage/api";

export function BudgetAlertBanner({ budget }: { budget: BudgetStatusResponse }): ReactElement | null {
  const { t } = useTranslation(ADMIN_NS);
  // Consider both windows and both ceiling kinds (USD + tokens) so a configured
  // token ceiling overage surfaces a banner too, not just a USD overage.
  const statuses = [
    budget.daily.usd_status,
    budget.monthly.usd_status,
    budget.daily.tokens_status,
    budget.monthly.tokens_status,
  ];
  const worst = statuses.includes("over")
    ? "over"
    : statuses.includes("approaching")
      ? "approaching"
      : "under";
  if (worst === "under") return null;
  const bg = worst === "over" ? "var(--g-danger)" : "var(--g-warn)";
  return (
    <div role="alert" data-status={worst} data-testid="ai-budget-banner"
      className="rounded-[var(--g-radius)] px-4 py-3 text-sm font-medium text-white"
      style={{ backgroundColor: bg }}>
      {t(`aiUsage.alert.${worst}`)}
    </div>
  );
}
export default BudgetAlertBanner;
